import { CaretLeftIcon } from '@radix-ui/react-icons';
import { Badge, Box, Flex, Heading, IconButton, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTable } from 'components/common/table';
import { TableProvider } from 'components/common/table/context';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import {
  CheckedContext,
  CheckedProvider,
} from 'contexts/layout/checked.context';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { LOCAL_TIMEZONE } from 'enums/env';
import { TABLES } from 'enums/tables';
import { TableIdentifier } from 'interfaces/cookies/i-app.cookie';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSortable } from 'interfaces/tables/sorting';
import { MachinePerformanceHelper } from 'lib_ts/classes/machine-performance.helper';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { BallType } from 'lib_ts/enums/machine.enums';
import {
  MAX_BREAKS_DELTA_IN,
  MAX_BREAKS_DELTA_IN_TRAJEKT_36,
  MAX_PLATE_DELTA_IN,
  MAX_VELO_DELTA_MPH,
} from 'lib_ts/enums/pitch-performance';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  IFireWithMetrics,
  IGroupedFires,
} from 'lib_ts/interfaces/i-pitch-performance';
import { useContext, useEffect, useMemo } from 'react';

const COMPONENT_NAME = 'PitchPerfShotTable';

const IDENTIFIER = TableIdentifier.PitchPerformanceShotList;
const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

interface IHoCProps {
  onDrillUp: () => void;
}

// wrap in an additional CheckedProvider and TableProvider so it can maintain a separate checked/table state than the PitchPerfGroupTable
export const PitchPerfShotTableHoC = ({ onDrillUp }: IHoCProps) => {
  const { allGroups, selectedPitchId } = usePitchPerformanceStore();

  const pitch = useMemo(
    () => allGroups.find((g) => g._id === selectedPitchId),
    [allGroups, selectedPitchId]
  );

  const shots = useMemo(() => {
    if (!pitch) {
      return [];
    }

    return MachinePerformanceHelper.getFireGroupMetrics(pitch);
  }, [allGroups, pitch]);

  return (
    <TableProvider>
      <CheckedProvider data={shots}>
        <PitchPerfShotTable pitch={pitch} shots={shots} onDrillUp={onDrillUp} />
      </CheckedProvider>
    </TableProvider>
  );
};

interface IProps extends IHoCProps {
  onDrillUp: () => void;
  pitch?: IGroupedFires;
  shots: IFireWithMetrics[];
}

const PitchPerfShotTable = ({ pitch, shots, onDrillUp }: IProps) => {
  const { tally, resetChecked, getChecked } = useContext(CheckedContext);

  const { loading, filters, setSelectedPitchId, setSelectedShotIds } =
    usePitchPerformanceStore();

  useEffect(() => {
    // keep selectedShotIds in sync with the checked items
    // we'll use selectedShotIds to show metrics/plots specific to the selected shots
    if (tally.checked > 0) {
      const selectedShots = shots.filter((s) => getChecked(s.shot._id));
      setSelectedShotIds(selectedShots.map((s) => s.shot._id));
      return;
    }

    setSelectedShotIds([]);
  }, [tally.key, tally.checked, shots, setSelectedShotIds]);

  const pagination: ITablePageable = {
    identifier: IDENTIFIER,
    total: shots.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: 'name',
      dir: -1,
    },
  };

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    // just a placeholder
    checkedActions: () => [],
  };

  const MAX_BREAKS_DELTA_INCHES =
    filters.ball_type === BallType.Trajekt_36
      ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
      : MAX_BREAKS_DELTA_IN;

  const columns: ITableColumn[] = [
    {
      label: 'Fired Date',
      key: 'shot._created',
      sortRowsFn: (a: IFireWithMetrics, b: IFireWithMetrics, dir) =>
        (a.shot._created > b.shot._created ? -1 : 1) * dir,
      formatFn: (m: IFireWithMetrics) => (
        <Text>
          {format(parseISO(m.shot._created), 'MM/dd/yyyy HH:mm z', {
            timeZone: LOCAL_TIMEZONE,
          })}
        </Text>
      ),
    },
    {
      label: 'common.speed',
      subLabel: 'mph',
      key: '_speed',
      sortRowsFn: (a: IFireWithMetrics, b: IFireWithMetrics, dir) =>
        (a.speedMPH > b.speedMPH ? 1 : -1) * dir,
      formatFn: (m: IFireWithMetrics) => (
        <Flex direction="column" pt="2" pb="2">
          <Box>
            <Text>{m.speedMPH.toFixed(1)}</Text>
          </Box>
          <Box>
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              Target: {m.target.speedMPH.toFixed(1)}
            </Text>
          </Box>
          <Box>
            <Badge
              color={
                Math.abs(m.delta.speedMPH) > MAX_VELO_DELTA_MPH
                  ? RADIX.COLOR.DANGER
                  : RADIX.COLOR.SUCCESS
              }
            >
              {m.delta.speedMPH > 0 && '+'}
              {m.delta.speedMPH.toFixed(1)}
            </Badge>
          </Box>
        </Flex>
      ),
    },
    {
      label: 'common.h-break',
      subLabel: 'in',
      key: '_breakX',
      sortRowsFn: (a: IFireWithMetrics, b: IFireWithMetrics, dir) =>
        (a.break.xInches > b.break.xInches ? 1 : -1) * dir,
      formatFn: (m: IFireWithMetrics) => (
        <Flex direction="column" pt="2" pb="2">
          <Box>
            <Text>{-m.break.xInches.toFixed(1)}</Text>
          </Box>
          <Box>
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              Target: {-m.target.break.xInches.toFixed(1)}
            </Text>
          </Box>
          <Box>
            <Badge
              color={
                Math.abs(m.delta.break.xInches) > MAX_BREAKS_DELTA_INCHES
                  ? RADIX.COLOR.DANGER
                  : RADIX.COLOR.SUCCESS
              }
            >
              {-m.delta.break.xInches.toFixed(1)}
              {-m.delta.break.xInches > 0 && '+'}
            </Badge>
          </Box>
        </Flex>
      ),
    },
    {
      label: 'common.v-break',
      subLabel: 'in',
      key: '_breakZ',
      sortRowsFn: (a: IFireWithMetrics, b: IFireWithMetrics, dir) =>
        (a.break.zInches > b.break.zInches ? 1 : -1) * dir,
      formatFn: (m: IFireWithMetrics) => (
        <Flex direction="column" pt="2" pb="2">
          <Box>
            <Text>{m.break.zInches.toFixed(1)}</Text>
          </Box>
          <Box>
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              Target: {m.target.break.zInches.toFixed(1)}
            </Text>
          </Box>
          <Box>
            <Badge
              color={
                Math.abs(m.delta.break.zInches) > MAX_BREAKS_DELTA_INCHES
                  ? RADIX.COLOR.DANGER
                  : RADIX.COLOR.SUCCESS
              }
            >
              {m.delta.break.zInches > 0 && '+'}
              {m.delta.break.zInches.toFixed(1)}
            </Badge>
          </Box>
        </Flex>
      ),
    },
    {
      label: 'common.plate-x',
      subLabel: 'in',
      key: '_plateX',
      sortRowsFn: (a: IFireWithMetrics, b: IFireWithMetrics, dir) =>
        (a.plate.plate_x > b.plate.plate_x ? 1 : -1) * dir,
      formatFn: (m: IFireWithMetrics) => (
        <Flex direction="column" pt="2" pb="2">
          <Box>
            <Text>{(m.plate.plate_x * FT_TO_INCHES).toFixed(1)}</Text>
          </Box>
          <Box>
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              Target: {(m.target.plate.plate_x * FT_TO_INCHES).toFixed(1)}
            </Text>
          </Box>
          <Box>
            <Badge
              color={
                Math.abs(m.delta.plate.plate_x * FT_TO_INCHES) >
                MAX_PLATE_DELTA_IN
                  ? RADIX.COLOR.DANGER
                  : RADIX.COLOR.SUCCESS
              }
            >
              {m.delta.plate.plate_x > 0 && '+'}
              {(m.delta.plate.plate_x * FT_TO_INCHES).toFixed(1)}
            </Badge>
          </Box>
        </Flex>
      ),
    },
    {
      label: 'common.plate-z',
      subLabel: 'in',
      key: '_plateZ',
      sortRowsFn: (a: IFireWithMetrics, b: IFireWithMetrics, dir) =>
        (a.plate.plate_z > b.plate.plate_z ? 1 : -1) * dir,
      formatFn: (m: IFireWithMetrics) => (
        <Flex direction="column" pt="2" pb="2">
          <Box>
            <Text>{(m.plate.plate_z * FT_TO_INCHES).toFixed(1)}</Text>
          </Box>
          <Box>
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              Target: {(m.target.plate.plate_z * FT_TO_INCHES).toFixed(1)}
            </Text>
          </Box>
          <Box>
            <Badge
              color={
                Math.abs(m.delta.plate.plate_z * FT_TO_INCHES) >
                MAX_PLATE_DELTA_IN
                  ? RADIX.COLOR.DANGER
                  : RADIX.COLOR.SUCCESS
              }
            >
              {m.delta.plate.plate_z > 0 && '+'}
              {(m.delta.plate.plate_z * FT_TO_INCHES).toFixed(1)}
            </Badge>
          </Box>
        </Flex>
      ),
    },
  ];

  const drillUp = () => {
    // reset this table's internal checked state
    resetChecked();

    onDrillUp();
    setSelectedPitchId('');
    setSelectedShotIds([]);
  };

  return (
    <ErrorBoundary componentName={COMPONENT_NAME}>
      <Box>
        <CommonTable
          id={COMPONENT_NAME}
          toolbarContent={
            <Box>
              <Flex align="center" gap="2" mb="1">
                <IconButton
                  size={RADIX.BUTTON.SIZE.XS}
                  variant="soft"
                  color={RADIX.COLOR.NEUTRAL}
                  onClick={drillUp}
                >
                  <CaretLeftIcon />
                </IconButton>
                <Heading size="4">{pitch?.name}</Heading>
                <Badge color={RADIX.COLOR.SECONDARY}>
                  {shots.length} Shots
                </Badge>
              </Flex>
              <Text color={RADIX.COLOR.SECONDARY}>
                Details for each shot of the selected pitch
              </Text>
            </Box>
          }
          displayColumns={columns}
          displayData={shots}
          loading={loading}
          {...pagination}
          {...checkable}
          {...sort}
        />
      </Box>
    </ErrorBoundary>
  );
};
